import { action, observable, runInAction, toJS } from "mobx";
import {
  AGENT_EVENTS,
  CLIENT_EVENTS,
  BLOCK,
  LOCAL_ACTIONS
} from "Pages/Dashboard/Copilot/agent/agentContract";
import { createAgentTransport } from "Pages/Dashboard/Copilot/agent/AgentTransport";
import { sequenceStepsToPreview } from "Pages/Dashboard/Copilot/agent/sequenceSteps";
import {
  DIRECT_READS,
  runDirectRead
} from "Pages/Dashboard/Copilot/agent/directReads";
import { makeApi, URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { getCopilotContext } from "Pages/Dashboard/Copilot/agent/copilotContext";
import {
  agentBaseUrl,
  ensureCopilotCredential
} from "Pages/Dashboard/Copilot/agent/SseAgentTransport";
import { Utils } from "Utils/utils";
import {
  fetchPage,
  removeRowById,
  resetTables
} from "Pages/Dashboard/Copilot/agent/mockProspects";
import {
  createConversation,
  listConversations,
  getConversation,
  saveConversation,
  renameConversationApi,
  deleteConversationApi
} from "Pages/Dashboard/Copilot/agent/copilotConversationsApi";

// Result tables: 10 rows by default; the panel can switch to 25, never more.
const PAGE_SIZE = 10;
export const TABLE_PAGE_SIZES = [10, 25];

// Chat history is owned server-side (CM copilot_conversation) — no localStorage.
const TITLE_MAX = 48;

// Docked-canvas width. Clamped on both read and write so a stale or hand-edited
// value can never squeeze the thread down to nothing.
const CANVAS_WIDTH_KEY = "sg_copilot_canvas_width";
const CANVAS_MIN = 380;
const CANVAS_MAX_RATIO = 0.62; // never let the canvas own more than ~2/3 of the page
const CANVAS_DEFAULT = 620;

export const clampCanvasWidth = px => {
  const viewport = typeof window === "undefined" ? 1440 : window.innerWidth;
  const max = Math.max(CANVAS_MIN, Math.round(viewport * CANVAS_MAX_RATIO));
  return Math.min(max, Math.max(CANVAS_MIN, Math.round(px)));
};

const readCanvasWidth = () => {
  try {
    const saved = Number(localStorage.getItem(CANVAS_WIDTH_KEY));
    return clampCanvasWidth(saved > 0 ? saved : CANVAS_DEFAULT);
  } catch (e) {
    return CANVAS_DEFAULT;
  }
};

/**
 * State for the /copilot chat page. All agent traffic funnels through
 * applyAgentEvent (one reducer for the whole stream, mirroring how
 * DeepResearch funnels SSE events into applyProgressEvent), so swapping the
 * mock transport for the real SSE one later touches nothing here.
 *
 * MobX 5: `tables` is an observable.map because dynamic keys on plain
 * observable objects are not reactive in v5.
 */
class CopilotStore {
  @observable conversationId = null;
  @observable connected = false;
  @observable messages = [];
  @observable streaming = false; // an assistant turn is in flight
  // Text the user sent while a turn was in flight — dispatched to the
  // transport in order as each prior turn ends, so nothing typed is dropped.
  @observable queuedMessages = [];
  @observable awaiting = null; // turn_end.await of the last turn
  @observable awaitingHint = null; // optional composer ghost text from the agent
  @observable suggestions = []; // one-tap reply chips (agent-generated)
  @observable sidePanel = { open: false, tableId: null };
  @observable previewPanel = { open: false, blockId: null };
  // Entity detail (seller profile, inbox, list…) opened from an entity card.
  @observable entityPanel = { open: false, blockId: null, itemId: null };
  // Width of the docked canvas (table / sequence preview) in px. The canvas
  // sits beside the thread rather than over it, so this is a real split, and
  // the user's chosen ratio is worth keeping across reloads.
  @observable canvasWidth = readCanvasWidth();
  @observable tables = observable.map();
  @observable historyOpen = false;
  // One-shot channel: hero prompt rows prefill the composer through this.
  @observable composerDraft = "";
  // Thread follow state: pinned = at the bottom, following the stream.
  @observable threadPinned = true;
  // Incrementing signal: the jump pill asks ChatThread to scroll down.
  @observable threadJumpRequest = 0;
  // History drawer, loaded from CM. [{id, title, messageCount, lastActivityTs}]
  @observable conversations = [];
  // True while a chat is being fetched from the server (switching history) —
  // the thread shows a spinner instead of the old chat freezing then snapping.
  @observable loadingConversation = false;
  conversationTitle = "";

  transport = null;
  disconnectFn = null;
  // blockId -> observable block object (lookup only, not itself observable)
  blockIndex = {};
  // In-flight lazy-create promise, so concurrent persists share one create.
  _createPromise = null;

  // Start a fresh (unsaved) chat. conversationId stays null until the first
  // message, when it's created server-side (lazy — no empty rows for a visit
  // that never sends anything). The transport bootstraps its own MCP
  // conversation independently of this id.
  @action
  startConversation = () => {
    if (this.connected) return;
    // A fresh transport means nothing is in flight. `streaming` is only ever
    // cleared by turn_end, so a turn that never ended — agent restarted, SSE
    // dropped, credential failed — left it stuck true forever. Every later
    // message then hit the `if (queued) return;` branch in sendUserMessage and
    // sat in queuedMessages, which is only drained on turn_end: the UI showed
    // "Queued — sending after the current response" and silently swallowed
    // every message from then on, with no error and no request made.
    this.streaming = false;
    this.queuedMessages = [];
    this.awaiting = null;
    this.conversationId = null;
    this.transport = createAgentTransport();
    this.disconnectFn = this.transport.connect(
      null,
      this.applyAgentEvent,
      null,
      this.consumePendingFolderId()
    );
    this.connected = true;
  };

  // ── Global floating panel (CopilotWidget) ────────────────────────────
  // Ambient by default: the panel is OPEN. Closing it is PAGE-SCOPED — it
  // hides for the page you're on, and reopens on the next navigation. The
  // copilot is present, not summoned.
  @observable panelOpen = true;

  // Wide mode: the expand control grows the docked panel in place (never
  // navigates away). Persisted like the open state.
  @observable panelWide = (() => {
    try {
      return localStorage.getItem("sg_copilot_panel_wide") === "1";
    } catch (e) {
      return false;
    }
  })();

  @action
  togglePanelWide = () => {
    this.panelWide = !this.panelWide;
    try {
      localStorage.setItem("sg_copilot_panel_wide", this.panelWide ? "1" : "0");
    } catch (e) {
      /* storage blocked */
    }
  };

  // ── Live pulse (nudge toasts) ────────────────────────────────────────
  // Polls /copilot/pulse; server diffs cheap reads and returns typed events
  // ("X just replied"). One toast visible at a time; extras batch.
  @observable pulseToast = null;
  _pulseTimer = null;
  _toastTimer = null;

  startPulse = () => {
    if (this._pulseTimer) return;
    const poll = async () => {
      // Credential comes from the session, not from a live transport: the pulse
      // runs on pages that have never opened a conversation, where
      // transport.apiKey is always empty.
      const baseUrl = agentBaseUrl();
      const apiKey = await ensureCopilotCredential();
      if (!baseUrl || !apiKey) return;
      try {
        const res = await fetch(`${baseUrl}/copilot/pulse`, {
          headers: { "x-api-key": apiKey }
        });
        if (!res.ok) return;
        const { events = [] } = await res.json();
        if (events.length) this.showPulseToast(events);
      } catch (e) {
        /* pulse is best-effort */
      }
    };
    this._pulseTimer = setInterval(poll, 45000);
    setTimeout(poll, 6000); // first baseline poll shortly after load
  };

  @action
  showPulseToast = events => {
    const first = events[0];
    this.pulseToast =
      events.length === 1
        ? first
        : {
            ...first,
            title: `${events.length} updates — ${first.title.toLowerCase()} & more`
          };
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(this.dismissPulseToast, 10000);
  };

  @action
  dismissPulseToast = () => {
    this.pulseToast = null;
  };

  // ── Ambient page insights ────────────────────────────────────────────
  // {status: "idle"|"loading"|"ready"|"empty", surface, data} — auto-loaded
  // on every route change while the panel is open.
  @observable pageInsights = { status: "idle", surface: "", data: null };
  _insightsSeq = 0;

  // Conversation-free insights fetch. Callable from any page (no transport
  // needed) and safe to call concurrently -- the orchestrator dedups in-flight
  // requests and caches the result, so N callers cost one model call.
  // Resolves to the payload, or null for "nothing to say" / any failure --
  // callers must render a fallback rather than an empty answer.
  fetchInsights = async (surface = "", entityId = "") => {
    const baseUrl = agentBaseUrl();
    if (!baseUrl || !surface) return null;
    // Same session-minted sgct_ credential the transport uses. Reading it from
    // localStorage (as this used to) always found nothing, because the token is
    // held in memory by design -- which is why insights never once returned.
    const apiKey = await ensureCopilotCredential();
    if (!apiKey) return null;
    try {
      // A cold insights call runs several tool fetches plus one model call
      // server-side, and the model alone is allowed up to 120s. A short client
      // timeout aborts the first (uncached) call every time, which then looks
      // like a failure even though the server completes and caches the result.
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 90000);
      const res = await fetch(
        `${baseUrl}/copilot/insights?surface=${encodeURIComponent(
          surface
        )}&id=${encodeURIComponent(entityId)}`,
        { headers: { "x-api-key": apiKey }, signal: controller.signal }
      );
      clearTimeout(timer);
      return res.status === 200 ? await res.json() : null;
    } catch (e) {
      return null;
    }
  };

  @action
  loadPageInsights = context => {
    const route = context?.page?.route || "";
    const entityId = context?.page?.entity?.id || "";
    const surface =
      route === "sequences" && entityId ? "sequences_detail" : route;
    if (!surface) return;
    if (
      this.pageInsights.surface === surface + entityId &&
      this.pageInsights.status === "ready"
    ) {
      return; // same page, already shown
    }
    const seq = ++this._insightsSeq;
    this.pageInsights = { status: "loading", surface: surface + entityId, data: null };
    // Delegates to fetchInsights so there is one authenticated path, not two —
    // this used to carry its own copy, reading a credential that was never set.
    this.fetchInsights(surface, entityId).then(
      action(data => {
        if (seq !== this._insightsSeq) return; // navigated away meanwhile
        this.pageInsights = data
          ? { status: "ready", surface: surface + entityId, data }
          : { status: "empty", surface: surface + entityId, data: null };
      })
    );
  };

  @action
  openPanel = () => {
    this.panelOpen = true;
    if (!this.connected) this.startConversation();
  };

  // Where the user closed it — the panel stays closed on THIS path only and
  // comes back on the next navigation.
  panelClosedPath = "";

  @action
  closePanel = (path = "") => {
    this.panelOpen = false;
    this.panelClosedPath = path;
  };

  // Single seeding API for every "ask copilot about this" entry point:
  // opens the panel and either sends the query straight away or leaves it
  // in the composer for the user to edit first.
  @action
  askWithQuery = (query = "", { send = true } = {}) => {
    this.openPanel();
    if (!query) return;
    if (send) {
      this.sendUserMessage(query);
    } else {
      this.composerDraft = query;
    }
  };

  /**
   * Post a message into the thread from the app itself, with no turn behind it.
   * Used when something completed outside the conversation (an import from a
   * popup, say) and the thread would otherwise just sit there looking like
   * nothing happened.
   */
  @action
  addLocalNote = (markdown = "") => {
    if (!markdown) return;
    const stamp = Date.now();
    this.messages.push({
      id: `local_${stamp}`,
      role: "assistant",
      blocks: [{ id: `blocal_${stamp}`, type: BLOCK.TEXT, markdown }],
      ts: stamp,
      streaming: false
    });
  };

  // Page context captured at CLICK time. Inline entry points navigate to
  // /copilot before the message is sent, by which point the originating page
  // has unmounted and its context adapter is gone — reading the context at
  // send time would hand the orchestrator an empty selection. Consumed once,
  // by the first message of the new conversation.
  pendingContext = null;

  stagePendingContext = () => {
    this.pendingContext = getCopilotContext();
  };

  nextContext = () => {
    const staged = this.pendingContext;
    this.pendingContext = null;
    return staged || getCopilotContext();
  };

  // The one way an inline affordance hands off to the full copilot: snapshot
  // the page context now, stage the query, then navigate. `send: false`
  // drops it into the composer instead so the user can adjust it first.
  openCopilotWith = (history, query = "", { send = true } = {}) => {
    this.stagePendingContext();
    if (send) {
      this.setPendingAutoSend(query);
    } else {
      this.setComposerDraft(query);
    }
    history.push("/copilot/new");
  };

  @action
  applyAgentEvent = evt => {
    if (!evt || !evt.type) return;
    switch (evt.type) {
      case AGENT_EVENTS.TURN_START: {
        this.messages.push({
          id: evt.turnId,
          role: "assistant",
          blocks: [],
          ts: Date.now(),
          streaming: true
        });
        this.streaming = true;
        this.awaiting = null;
        this.suggestions = [];
        break;
      }
      case AGENT_EVENTS.SUGGESTIONS: {
        if (!this.streaming) {
          // Items are typed ({kind: "reply"|"action", label, ...}); plain
          // strings from an older agent normalize to reply chips.
          this.suggestions = (evt.items || []).map(item =>
            typeof item === "string" ? { kind: "reply", label: item } : item
          );
        }
        break;
      }
      case AGENT_EVENTS.BLOCK_START: {
        const message = this.findMessage(evt.turnId);
        if (!message || !evt.block) break;
        message.blocks.push(evt.block);
        // keep the observable copy MobX created inside the array
        const observableBlock = message.blocks[message.blocks.length - 1];
        this.blockIndex[evt.block.id] = observableBlock;
        if (evt.block.type === BLOCK.TABLE_REF) {
          this.registerTable(evt.block);
        }
        // Live mutations auto-open their preview panel (hero moments).
        // Seller-profile drafts share the same panel mechanism so the user
        // can edit/approve/discard in the right-side panel, same as how a
        // generated sequence is reviewed.
        if (
          (evt.block.type === BLOCK.LIVE_PREVIEW || evt.block.type === BLOCK.SELLER_PROFILE_PREVIEW) &&
          evt.block.autoOpen !== false
        ) {
          this.openPreviewPanel(evt.block.id);
        }
        break;
      }
      case AGENT_EVENTS.BLOCK_DELTA: {
        const block = this.blockIndex[evt.blockId];
        if (block) {
          block.markdown = (block.markdown || "") + (evt.delta || "");
        }
        break;
      }
      case AGENT_EVENTS.BLOCK_UPDATE: {
        const block = this.blockIndex[evt.blockId];
        if (!block || !evt.patch) break;
        Object.keys(evt.patch).forEach(key => {
          // Paged rows APPEND. A whole-array patch would drop anything the user
          // revealed while the request was in flight, and the agent no longer
          // holds the rows to send back anyway.
          if (key === "appendSequences") {
            const existing = block.sequences || [];
            const seen = new Set(existing.map(s => s.id));
            block.sequences = existing.concat(
              (evt.patch.appendSequences || []).filter(s => !seen.has(s.id))
            );
            return;
          }
          block[key] = evt.patch[key];
        });
        // The add-to-list result comes back on the block; report it here so
        // every caller gets the same honest numbers (a partial add must never
        // read as a full one).
        if (evt.patch.listResult) {
          const { added = 0, skipped = 0, error = "" } = evt.patch.listResult;
          toasterState.setToastMsg(
            error
              ? "Couldn't add those contacts to the list."
              : skipped
                ? `Added ${added} to the list. ${skipped} skipped (no LinkedIn URL).`
                : `Added ${added} contact(s) to the list.`,
            error ? "failure" : "success"
          );
          if (!error) this.clearTableSelection(this.tableIdForBlock(block.id));
          if (this.pendingListExport === block.id) {
            this.pendingListExport = null;
            const { listId } = evt.patch.listResult;
            if (!error && added > 0) {
              this.exportListAfterAdd(listId, evt.patch.listResult.contactIds);
            } else if (listId) {
              // Nothing landed, so the throwaway list would just be litter.
              import("Components/popup/ExportProcessing").then(m =>
                m.exportProcessingState.setShowPopup(false)
              );
              import("Pages/Dashboard/Copilot/agent/contactActions").then(m =>
                m.deleteExportList(listId)
              );
            }
          }
        }
        // Binding a sequence to an already-rendered table must land before the
        // TABLE_REF-only sync below, which would otherwise skip the patch.
        if (evt.patch.sequenceId && block.tableId) {
          const bound = this.tables.get(block.tableId);
          if (bound) bound.sequenceId = evt.patch.sequenceId;
          // "Add contacts" belongs beside the sequence, not stacked under the
          // chat — open it in the docked panel on the right.
          this.addContactsPending = "";
          this.openTablePanel(block.tableId);
        }
        if (block.type === BLOCK.TABLE_REF) {
          this.syncTableFromBlock(block);
        }
        // A finished write means the page behind the drawer is now describing
        // stale state -- the blocker strip still says "no inbox assigned"
        // after the copilot has just assigned one. Pages watch this counter and
        // refresh the slice they own, rather than reloading.
        if (block.type === BLOCK.PROGRESS && evt.patch.status === "done") {
          this.mutationTick += 1;
        }
        break;
      }
      case AGENT_EVENTS.TURN_END: {
        const message = this.findMessage(evt.turnId);
        if (message) {
          message.streaming = false;
        }
        this.streaming = false;
        this.awaiting = evt.await || null;
        this.awaitingHint = evt.hint || null;
        this.persistConversation();
        this.dispatchNextQueuedMessage();
        break;
      }
      case AGENT_EVENTS.AGENT_ERROR: {
        this.streaming = false;
        this.messages.push({
          id: `err_${Date.now()}`,
          role: "assistant",
          blocks: [
            {
              id: `berr_${Date.now()}`,
              type: BLOCK.TEXT,
              markdown: evt.message || "Something went wrong. Please try again."
            }
          ],
          ts: Date.now(),
          streaming: false,
          error: true
        });
        this.dispatchNextQueuedMessage();
        break;
      }
      default:
        break;
    }
  };

  // A turn in flight no longer blocks sending — the message shows in the
  // thread right away (like the composer never left focus) and is queued for
  // dispatch the moment the current turn ends, instead of being dropped.
  @action
  sendUserMessage = text => {
    const trimmed = (text || "").trim();
    if (!trimmed) return;
    // Connect on the first message rather than when a surface opens. Opening the
    // drawer to read findings used to spin up an orchestrator conversation the
    // user never used. `connected` flips synchronously here and the transport
    // queues sends until its bootstrap completes, so this is safe to call
    // immediately before pushing the message.
    if (!this.connected) this.startConversation();
    if (!this.connected) return;
    Utils.mixpanelTrack("Copilot Query Sent", {
      inputQuery: trimmed,
      source: "message",
      conversationId: this.conversationId,
      queuedWhileStreaming: this.streaming,
      messageCount: this.messages.length
    });
    this.suggestions = [];
    const queued = this.streaming;
    this.messages.push({
      id: `u_${Date.now()}`,
      role: "user",
      text: trimmed,
      ts: Date.now(),
      queued
    });
    if (!this.conversationTitle) {
      this.conversationTitle =
        trimmed.length > TITLE_MAX
          ? `${trimmed.slice(0, TITLE_MAX)}…`
          : trimmed;
    }
    this.persistConversation();
    if (queued) {
      this.queuedMessages.push(trimmed);
      return;
    }
    // Set synchronously, not on the server's turn_start echo — `streaming`
    // is what the very next sendUserMessage call (fired a keystroke later)
    // checks to decide queue-vs-send. Waiting for the round-trip leaves a
    // window where a fast second message also reads `streaming` as false
    // and gets sent immediately instead of queued; both then reach the
    // orchestrator's runTurn() while it's still busy with the first, and
    // it silently drops whichever one loses — no turn_start, no error,
    // nothing rendered for it at all.
    this.streaming = true;
    // Try to answer from the app's own APIs first. Falls through to the normal
    // turn on anything but a clean hit, so the worst case is the old behaviour
    // plus one small classification call.
    const pageContext = this.nextContext();
    this.tryDirectAnswer(trimmed).then(answered => {
      if (answered) return;
      this.transport.send({
        type: CLIENT_EVENTS.USER_MESSAGE,
        text: trimmed,
        context: pageContext
      });
    });
  };

  /**
   * Answer a "show me what I own" question without a turn.
   *
   * Returns true only when the whole answer is on screen. Any doubt — unknown
   * intent, failed fetch, transport without a classifier — returns false and
   * the caller runs the normal turn.
   */
  tryDirectAnswer = async text => {
    try {
      if (!this.transport || !this.transport.classifyIntent) return false;
      // HARD CAP on the detour. Classification runs BEFORE the turn starts, so
      // its latency is dead air with no progress row on screen — exactly the
      // silence that makes a user re-send. Past 900ms the saving isn't worth
      // the wait: run the normal turn.
      const intent = await Promise.race([
        this.transport.classifyIntent(text),
        new Promise(resolve => setTimeout(() => resolve(null), 900))
      ]);
      if (!intent || !DIRECT_READS[intent]) return false;
      const card = await runDirectRead(intent);
      if (!card) return false;
      runInAction(() => {
        const blockId = `direct_${intent}_${Date.now()}`;
        const block = observable({
          id: blockId,
          type: BLOCK.SUMMARY_CARD,
          ...card
        });
        this.blockIndex[blockId] = block;
        this.messages.push({
          id: `a_${Date.now()}`,
          role: "assistant",
          blocks: [block],
          ts: Date.now(),
          streaming: false,
          // Marks the reply as answered without the model, so the transcript
          // sent back on rehydration can say so rather than looking like a
          // turn the agent produced and has no memory of.
          direct: true
        });
        this.streaming = false;
      });
      this.persistConversation();
      // A direct answer never fires turn_end — the only thing that drains the
      // queue. Without this, a message typed during classification sat queued
      // FOREVER with no error, and the user's only move was to re-send it
      // (hence duplicate bubbles with a single answer).
      this.dispatchNextQueuedMessage();
      return true;
    } catch (e) {
      return false;
    }
  };

  // Fires the next queued message (if any) the instant a turn ends. Flips
  // `streaming` back on immediately so the composer doesn't flash into its
  // idle state for the split second before the real turn_start arrives.
  @action
  dispatchNextQueuedMessage = () => {
    if (!this.queuedMessages.length) return;
    const next = this.queuedMessages.shift();
    const message = [...this.messages]
      .reverse()
      .find(m => m.role === "user" && m.queued && m.text === next);
    if (message) {
      message.queued = false;
    }
    this.streaming = true;
    this.transport.send({
      type: CLIENT_EVENTS.USER_MESSAGE,
      text: next,
      context: this.nextContext()
    });
  };

  // Stops the current turn. The UI settles once the resulting turn_end
  // event arrives — this only requests the stop, it doesn't flip
  // `streaming` itself.
  @action
  setThreadPinned = pinned => {
    this.threadPinned = pinned;
  };

  @action
  requestThreadJump = () => {
    this.threadJumpRequest += 1;
  };

  @action
  setComposerDraft = text => {
    this.composerDraft = text || "";
  };

  // One-shot channel for entry points that need the message actually sent
  // (not just prefilled) as soon as a fresh conversation connects — e.g. the
  // Sequences page's empty state / "Create Sequence" banner (SG-2182), which
  // auto-sends "Help me build a sequence." rather than waiting for the user
  // to hit enter like composerDraft's hero-prompt-row prefill does.
  @observable pendingAutoSend = null;

  @action
  setPendingAutoSend = text => {
    this.pendingAutoSend = text || null;
  };

  @action
  consumePendingAutoSend = () => {
    const text = this.pendingAutoSend;
    this.pendingAutoSend = null;
    return text;
  };

  // One-shot channel for the Sequences folder id the user was browsing when
  // they opened Copilot (SG-2182) — lets create_sequence place the new
  // sequence in that folder instead of always landing in the default
  // unfiled location. Consumed by startConversation, same pattern as
  // pendingAutoSend above.
  @observable pendingFolderId = null;

  @action
  setPendingFolderId = folderId => {
    this.pendingFolderId = folderId || null;
  };

  @action
  consumePendingFolderId = () => {
    const folderId = this.pendingFolderId;
    this.pendingFolderId = null;
    return folderId;
  };

  @action
  interrupt = () => {
    if (!this.streaming || !this.transport?.interrupt) return;
    this.transport.interrupt();
  };

  @action
  selectQuickReply = (blockId, option) => {
    const block = this.blockIndex[blockId];
    if (!block || block.selectedValue || this.streaming) return;
    block.selectedValue = option.value;
    this.transport.send({
      type: CLIENT_EVENTS.QUICK_REPLY_SELECTED,
      blockId,
      replyId: block.replyId,
      value: option.value
    });
  };

  @action
  clickCta = (blockId, chip = {}) => {
    if (this.streaming) return;
    const block = this.blockIndex[blockId];
    // Local-effect chips stay clickable (view table can be reopened); agent
    // actions render as consumed so they aren't double-fired.
    const isLocal =
      chip.actionId === LOCAL_ACTIONS.VIEW_TABLE ||
      chip.actionId === LOCAL_ACTIONS.NAVIGATE;
    if (chip.actionId === LOCAL_ACTIONS.VIEW_TABLE && chip.tableId) {
      this.openTablePanel(chip.tableId);
    }
    if (block && !isLocal) {
      if (block.clickedActionId) return;
      block.clickedActionId = chip.actionId;
    }
    this.transport.send({
      type: CLIENT_EVENTS.CTA_CLICKED,
      blockId,
      actionId: chip.actionId
    });
  };

  @action
  @action
  sendFeedback = (messageId, verdict) => {
    const message = this.findMessage(messageId);
    if (!message || message.feedback) return;
    message.feedback = verdict;
    const idx = this.messages.indexOf(message);
    const prevUser = this.messages
      .slice(0, idx)
      .reverse()
      .find(m => m.role === "user");
    const replyText = (message.blocks || [])
      .map(b => b.markdown || b.text || "")
      .join("\n");
    this.transport.sendFeedback({
      turnId: messageId,
      verdict,
      userText: prevUser ? prevUser.text : "",
      replyText
    });
  };

  respondConfirmation = (blockId, confirmed) => {
    const block = this.blockIndex[blockId];
    if (!block || block.resolution || this.streaming) return;
    block.resolution = confirmed ? "confirmed" : "cancelled";
    this.transport.send({
      type: CLIENT_EVENTS.CONFIRMATION_RESPONSE,
      blockId,
      confirmId: block.confirmId,
      confirmed
    });
  };

  @action
  sendActionResult = (blockId, actionId, status, data) => {
    this.transport.send({
      type: CLIENT_EVENTS.ACTION_RESULT,
      blockId,
      actionId,
      status,
      data
    });
  };

  @action
  openTablePanel = tableId => {
    if (!this.tables.has(tableId)) return;
    this.previewPanel = { open: false, blockId: null };
    this.entityPanel = { open: false, blockId: null, itemId: null };
    this.sidePanel = { open: true, tableId };
    const table = this.tables.get(tableId);
    if (!table.rows.length) {
      this.fetchTablePage(tableId, 1);
    }
  };

  @action
  closeTablePanel = () => {
    this.sidePanel = { open: false, tableId: null };
  };

  @action
  openPreviewPanel = blockId => {
    if (!this.blockIndex[blockId]) return;
    this.sidePanel = { open: false, tableId: null };
    this.entityPanel = { open: false, blockId: null, itemId: null };
    this.previewPanel = { open: true, blockId };
  };

  @action
  closePreviewPanel = () => {
    this.previewPanel = { open: false, blockId: null };
  };

  // Table the entity panel was opened from, so closing it goes back there.
  entityPanelReturnTableId = "";

  @action
  openEntityPanel = (blockId, itemId, returnTableId = "") => {
    const block = this.blockIndex[blockId];
    if (!block || !(block.items || []).some(item => item.id === itemId)) return;
    // Opened FROM a table: closing the detail must put the table back, not
    // leave the canvas empty with the selection gone.
    this.entityPanelReturnTableId = returnTableId || "";
    this.sidePanel = { open: false, tableId: null };
    this.previewPanel = { open: false, blockId: null };
    this.entityPanel = { open: true, blockId, itemId };
    // Panel opens on what the agent packed; the fuller record fills in behind it.
    const item = (block.items || []).find(row => row.id === itemId);
    this.enrichEntityDirect(block.kind, item);
  };

  @action
  closeEntityPanel = () => {
    this.entityPanel = { open: false, blockId: null, itemId: null };
    const back = this.entityPanelReturnTableId;
    this.entityPanelReturnTableId = "";
    if (back) this.openTablePanel(back);
  };

  // The card the entity panel is showing: { kind, item } or null.
  get activeEntityItem() {
    if (!this.entityPanel.open) return null;
    const block = this.blockIndex[this.entityPanel.blockId];
    if (!block) return null;
    const item = (block.items || []).find(
      candidate => candidate.id === this.entityPanel.itemId
    );
    return item ? { kind: block.kind, item } : null;
  }

  // Answers from a question_form block; queues like any other send if a turn
  // is somehow still streaming.
  @action
  submitQuestionForm = (blockId, answers) => {
    const block = this.blockIndex[blockId];
    if (!block || block.done) return;
    block.done = true;
    block.answers = answers;
    Utils.mixpanelTrack("Copilot Query Sent", {
      inputQuery: (answers || [])
        .map(a => `${a.question} ${a.answer || "(skipped)"}`)
        .join("; "),
      source: "question_form",
      conversationId: this.conversationId,
      messageCount: this.messages.length
    });
    this.transport.send({
      type: CLIENT_EVENTS.QUESTION_FORM_RESPONSE,
      blockId,
      answers
    });
  };

  // Approve (and optionally, already-edited) a seller_profile_preview card —
  // saves it server-side and resumes the conversation into the sequence
  // questions (SG-2182 in-chat seller-profile flow).
  @action
  approveSellerProfile = (blockId, profile) => {
    const block = this.blockIndex[blockId];
    if (!block || block.done) return;
    block.done = true;
    block.saved = true;
    Utils.mixpanelTrack("Copilot Query Sent", {
      source: "seller_profile_preview",
      conversationId: this.conversationId,
      messageCount: this.messages.length
    });
    this.transport.send({
      type: CLIENT_EVENTS.SELLER_PROFILE_APPROVED,
      blockId,
      profile
    });
  };

  @action
  discardSellerProfile = blockId => {
    const block = this.blockIndex[blockId];
    if (!block || block.done) return;
    block.done = true;
    block.saved = false;
    block.discarded = true;
    Utils.mixpanelTrack("Copilot Query Sent", {
      source: "seller_profile_preview_discard",
      conversationId: this.conversationId,
      messageCount: this.messages.length
    });
    this.transport.send({
      type: CLIENT_EVENTS.SELLER_PROFILE_DISCARDED,
      blockId
    });
  };

  @action
  submitWebsite = (blockId, website) => {
    const block = this.blockIndex[blockId];
    if (!block || block.done || !website || !website.trim()) return;
    block.done = true;
    Utils.mixpanelTrack("Copilot Query Sent", {
      source: "website_input",
      conversationId: this.conversationId,
      messageCount: this.messages.length
    });
    this.transport.send({
      type: CLIENT_EVENTS.WEBSITE_SUBMITTED,
      blockId,
      website: website.trim()
    });
  };

  // Suggestion chips below the composer: reply chips send text, action chips
  // drive the UI. Navigation chips are handled in the component (react-router
  // history lives there), so this returns false for them.
  @action
  applySuggestion = item => {
    if (!item || typeof item !== "object") return true;
    if (item.kind === "reply") {
      // `prompt` is what gets asked; `label` is only what the chip prints.
      // Without the split a chip can only ever ask its own caption, so it
      // cannot read "Step 3" and mean "break down step 3 by drop-off reason".
      this.sendUserMessage(item.prompt || item.label);
      return true;
    }
    if (item.actionId === LOCAL_ACTIONS.VIEW_TABLE && item.tableId) {
      this.openTablePanel(item.tableId);
      return true;
    }
    return false;
  };

  // Open a stored sequence in the docked preview panel. The agent fetches it
  // and emits a live_preview block, which BLOCK_START already auto-opens — so
  // this is a view, not a chat turn, and nothing is added to the transcript.
  @action
  viewSequence = (sequenceId, name = "") => {
    if (!sequenceId) return;
    this.transport.send({ type: CLIENT_EVENTS.VIEW_SEQUENCE, sequenceId, name });
  };

  /**
   * Open a sequence in the preview panel WITHOUT going through the agent.
   *
   * Viewing a record the user just clicked is a read the app can already do:
   * /cm/seq/{id} is session-authed and returns the full stored sequence, so the
   * panel gets every variant's subject and body instead of the trimmed shape the
   * MCP tool exposes. It also removes the agent from the path entirely — no SSE
   * round trip, no dependence on a live conversation, and it works on a thread
   * reopened after a refresh.
   *
   * The block is synthesised locally and registered in blockIndex, so
   * SequencePreviewPanel renders it exactly like an agent-emitted one.
   */
  @action
  openSequenceDirect = async (sequenceId, name = "") => {
    if (!sequenceId) return;
    const blockId = `local_seq_${sequenceId}`;
    if (!this.blockIndex[blockId]) {
      this.blockIndex[blockId] = observable({
        id: blockId,
        type: BLOCK.LIVE_PREVIEW,
        kind: "sequence_builder",
        title: name || "Sequence",
        status: "building",
        steps: [],
        hidden: true,
        sequenceId
      });
    }
    const block = this.blockIndex[blockId];
    block.status = "building";
    this.previewPanel = { open: true, blockId };

    try {
      const res = await makeApi({
        url: URLS.getSelectedSeqData.replace("<<seqID>>", sequenceId),
        method: "GET"
      });
      // Java sends null, not undefined — pull the field first, then default.
      const data = (res && res.data) || {};
      const sequence = data.response || data.data || data;
      const steps = sequenceStepsToPreview(sequence.steps || sequence.stages);
      runInAction(() => {
        block.title = sequence.name || name || "Sequence";
        block.steps = steps;
        block.status = steps.length ? "created" : "failed";
      });
      // Second call, deliberately after the steps render: how many contacts sit
      // on each step is useful but must never hold up the panel, and a failure
      // here leaves a working preview rather than an error.
      this.attachStepMetrics(blockId, sequenceId);
    } catch (e) {
      runInAction(() => {
        block.status = "failed";
      });
    }
  };

  /**
   * Contacts-per-step for an open sequence preview. Best effort by design: the
   * panel is already useful without it, so nothing here may surface an error.
   *
   * stepMetrics rows key off `order` (the step POSITION, same key the steps map
   * uses) and carry `count`. Only that pairing is read here — the nested
   * per-variant figures are left alone rather than guessed at.
   */
  attachStepMetrics = async (blockId, sequenceId) => {
    try {
      const res = await makeApi({
        url: URLS.getStepLevelMetrics.replace("<<sequenceId>>", sequenceId),
        method: "POST",
        data: { stepMetrics: [] }
      });
      const rows = ((res && res.data) || {}).stepMetrics || [];
      if (!rows.length) return;
      const byOrder = {};
      rows.forEach(row => {
        if (row && row.order !== "all" && row.order != null) {
          byOrder[String(row.order)] = row.count;
        }
      });
      const block = this.blockIndex[blockId];
      if (!block || !block.steps) return;
      runInAction(() => {
        block.steps = block.steps.map((step, index) => {
          const count = byOrder[String(index + 1)];
          return typeof count === "number" ? { ...step, contacts: count } : step;
        });
      });
    } catch (e) {
      /* metrics are a bonus: a working preview beats an error banner */
    }
  };

  /**
   * Fill in an entity panel record straight from the app's own API.
   *
   * Only kinds whose detail endpoint is unambiguous. Contact and company
   * records are deliberately NOT here — those come from prospecting search and
   * are left on their existing path.
   */
  @action
  enrichEntityDirect = async (kind, item) => {
    if (kind !== "task" || !item || !item.id) return;
    try {
      const res = await makeApi({
        url: URLS.getPerformTaskData.replace("<<taskId>>", item.id),
        method: "GET"
      });
      const data = (res && res.data) || {};
      const record = data.response || data.data || data;
      if (!record || typeof record !== "object") return;
      runInAction(() => {
        // Merge under what the agent already showed: the fetch is the fuller
        // record, but a field the card displayed should not vanish.
        item.detail = { ...(item.detail || {}), ...record };
      });
    } catch (e) {
      /* the card's own fields still render */
    }
  };

  /**
   * Ask the agent for the next page of a sequence_list. The spinner is set here
   * rather than in the block so a re-render can't lose it; the agent clears it
   * with the block_update that carries the new rows.
   */
  @action
  loadMoreSequences = blockId => {
    const block = this.blockIndex[blockId];
    if (!block || block.loading || block.exhausted) return;
    block.loading = true;
    block.loadError = false;
    // The cursor lives on the block, so this still works on a conversation
    // reopened after a refresh — the agent keeps no paging state of its own.
    this.transport.send({
      type: CLIENT_EVENTS.LOAD_MORE_SEQUENCES,
      blockId,
      page: block.nextPage || 2,
      pageSize: block.pageSize || 25,
      status: block.statusFilter,
      name: block.nameFilter,
      // Without these the next page would come from the UNFILTERED population
      // while the total still counts the filtered one.
      createdAfter: block.createdAfterFilter,
      createdBefore: block.createdBeforeFilter
    });
  };

  @action
  setCanvasWidth = px => {
    this.canvasWidth = clampCanvasWidth(px);
    try {
      localStorage.setItem(CANVAS_WIDTH_KEY, String(this.canvasWidth));
    } catch (e) {
      /* storage unavailable: the split still works, it just won't persist */
    }
  };

  get canvasOpen() {
    return this.sidePanel.open || this.previewPanel.open || this.entityPanel.open;
  }

  get activePreviewBlock() {
    return this.previewPanel.blockId
      ? this.blockIndex[this.previewPanel.blockId]
      : null;
  }

  @action
  /** Re-fetch the visible page, bypassing the server's page cache. */
  @action
  refreshTablePage = tableId => {
    const table = this.tables.get(tableId);
    if (table) this.fetchTablePage(tableId, table.currentPage || 1, true);
  };

  fetchTablePage = (tableId, page = 1, refresh = false) => {
    const table = this.tables.get(tableId);
    if (!table) return;
    table.loading = true;
    // Live transport serves real paged results; the mock uses local data.
    const source = this.transport?.fetchTablePage
      ? this.transport.fetchTablePage(tableId, page, table.pageSize, undefined, refresh)
      : fetchPage(tableId, page, table.pageSize);
    source
      .then(result => {
        runInAction(() => {
          table.rows = result.rows;
          table.totalCount = result.totalCount;
          table.currentPage = result.page;
          // A page can come back empty for a reason (e.g. the plan's page cap);
          // carry that message so the panel explains it instead of a bare
          // "No records on this page".
          table.notice = result.notice || "";
          table.noticeType = result.noticeType || "";
          // The server owns the effective page size (it clamps to its max);
          // trusting our local copy would miscount totalPages the moment
          // they diverge.
          if (result.pageSize) {
            table.pageSize = result.pageSize;
          }
          if (result.columns && result.columns.length) {
            table.columns = result.columns;
          }
          table.loading = false;
        });
      })
      .catch(() => {
        // A table from a reopened chat has no server-side result set anymore —
        // but its block carries the search spec, so silently re-materialize it
        // in the current conversation and retry the page ONCE. Only when that
        // also fails (or the spec predates this feature) does the panel show
        // the stale state, which offers a manual reload.
        const canRestore =
          table.kind &&
          table.args &&
          !table.restoreAttempted &&
          this.transport &&
          this.transport.restoreTable;
        if (canRestore) {
          runInAction(() => {
            table.restoreAttempted = true;
          });
          this.transport
            .restoreTable(tableId, {
              kind: table.kind,
              args: table.args,
              title: table.title,
              totalCount: table.totalCount,
              searchId: table.searchId,
              blockId: table.blockId
            })
            .then(() => this.fetchTablePage(tableId, page))
            .catch(() => {
              runInAction(() => {
                table.loading = false;
                if (!table.rows || !table.rows.length) {
                  table.stale = true;
                }
              });
            });
          return;
        }
        runInAction(() => {
          table.loading = false;
          if (!table.rows || !table.rows.length) {
            table.stale = true;
          }
        });
      });
  };

  // Manual reload from the stale panel state: clear the flags and go through
  // the normal fetch → restore-on-miss path again.
  @action
  retryStaleTable = tableId => {
    const table = this.tables.get(tableId);
    if (!table) return;
    table.stale = false;
    table.restoreAttempted = false;
    this.fetchTablePage(tableId, 1);
  };

  // Row remove only applies to the local mock data source.
  get supportsRowRemove() {
    return !(this.transport && this.transport.fetchTablePage);
  }

  @action
  toggleHistory = () => {
    this.historyOpen = !this.historyOpen;
  };

  // Hover-to-open history drawer. The trigger button and the drawer share these
  // handlers; a short close delay bridges the gap between them so moving the
  // cursor from the button into the drawer (or back) never flickers it shut.
  _historyCloseTimer = null;

  @action
  openHistoryHover = () => {
    if (this._historyCloseTimer) {
      clearTimeout(this._historyCloseTimer);
      this._historyCloseTimer = null;
    }
    this.historyOpen = true;
  };

  closeHistoryHover = () => {
    if (this._historyCloseTimer) clearTimeout(this._historyCloseTimer);
    this._historyCloseTimer = setTimeout(() => {
      this.historyOpen = false;
      this._historyCloseTimer = null;
    }, 200);
  };

  @action
  newChat = () => {
    this.persistConversation();
    this.reset();
    this.startConversation();
  };

  // Load the member's chat list for the history drawer (server-side).
  @action
  loadConversations = async () => {
    try {
      const list = await listConversations();
      runInAction(() => {
        this.conversations = list || [];
      });
    } catch (e) {
      /* drawer is best-effort */
    }
  };

  // Persist the thread to CM. Snapshots messages/title/id SYNCHRONOUSLY up
  // front so a reset() that runs during the awaits (e.g. switching chats) can't
  // corrupt the save. Lazily creates the conversation on the first save (so a
  // visit that never sends anything leaves no empty row), then upserts. Sets
  // conversationId when created so the component can sync the URL.
  persistConversation = async () => {
    const messages = toJS(this.messages);
    if (!messages.length) return;
    const title = this.conversationTitle;
    let id = this.conversationId;
    try {
      if (!id) {
        // Share a single in-flight create across concurrent callers (e.g.
        // sendUserMessage and the TURN_END handler both firing while
        // conversationId is still null), or each would POST a new row.
        if (!this._createPromise) {
          this._createPromise = createConversation(title);
        }
        const created = await this._createPromise;
        this._createPromise = null;
        if (!created || !created.id) return;
        id = created.id;
        runInAction(() => {
          if (!this.conversationId) this.conversationId = id;
        });
      }
      const saved = await saveConversation(id, title, messages);
      // Refresh the drawer entry (title/activity) without a full reload.
      if (saved) {
        runInAction(() => {
          const rest = this.conversations.filter(c => c.id !== id);
          this.conversations = [
            {
              id,
              title,
              messageCount: messages.length,
              lastActivityTs: Date.now()
            },
            ...rest
          ];
        });
      }
    } catch (e) {
      /* persistence is best-effort; the live turn still works */
    }
  };

  @action
  loadConversation = async id => {
    if (!id || id === this.conversationId) return;
    runInAction(() => {
      this.loadingConversation = true;
    });
    try {
      await this.persistConversation();
      const conv = await getConversation(id);
      if (!conv || !Array.isArray(conv.messages)) return;
      runInAction(() => {
        this.reset();
      this.conversationId = conv.id;
      this.conversationTitle = conv.title || "";
      this.messages = conv.messages.map(message => ({
        ...message,
        streaming: false
      }));
      // Rebuild the block lookup + table registry from the restored thread so
      // table cards stay clickable. A fresh transport handles new messages.
      this.messages.forEach(message => {
        (message.blocks || []).forEach((block, index) => {
          const observableBlock = message.blocks[index];
          this.blockIndex[block.id] = observableBlock;
          if (block.type === BLOCK.TABLE_REF) {
            this.registerTable(observableBlock);
          }
        });
      });
      this.transport = createAgentTransport();
      this.disconnectFn = this.transport.connect(
        this.conversationId,
        this.applyAgentEvent,
        this.buildRehydrationHistory()
      );
      this.connected = true;
      });
    } finally {
      runInAction(() => {
        this.loadingConversation = false;
      });
    }
  };

  // Prior turns of the reopened thread, as {role, text}, so the fresh server
  // conversation is seeded with context. Only the conversational TEXT carries
  // over — server-side artifacts (tables, saved lists) are gone, which the
  // orchestrator is told to handle by offering to re-run.
  buildRehydrationHistory = () => {
    return (this.messages || [])
      .map(m => {
        if (m.role === "user") {
          return { role: "user", text: (m.text || "").trim() };
        }
        if (m.role === "assistant" && !m.error) {
          const text = (m.blocks || [])
            .filter(b => b.type === BLOCK.TEXT && b.markdown)
            .map(b => b.markdown)
            .join("\n\n")
            .trim();
          return { role: "assistant", text };
        }
        return null;
      })
      .filter(entry => entry && entry.text);
  };

  // Removes from the drawer + soft-deletes server-side. If the deleted chat is
  // the open one, the caller navigates to /copilot/new (the URL drives the
  // fresh-chat reset), so this doesn't touch the live conversation itself.
  @action
  deleteConversation = async id => {
    this.conversations = this.conversations.filter(item => item.id !== id);
    try {
      await deleteConversationApi(id);
    } catch (e) {
      /* best-effort; the row is already gone from the drawer */
    }
  };

  // Rename a chat (title only — the stored thread is left intact). Optimistic:
  // the drawer entry and, if it's the open chat, the live title update at once;
  // the PUT persists in the background. Empty/blank names are ignored.
  @action
  renameConversation = async (id, title) => {
    const trimmed = (title || "").trim();
    if (!trimmed) return;
    this.conversations = this.conversations.map(item =>
      item.id === id ? { ...item, title: trimmed } : item
    );
    if (id === this.conversationId) {
      this.conversationTitle = trimmed;
    }
    try {
      await renameConversationApi(id, trimmed);
    } catch (e) {
      /* best-effort; the drawer already reflects the new name */
    }
  };

  // Row-level remove from the side panel (client-side in v1). Keeps the
  // chat's table_ref cards in sync so counts never diverge.
  @action
  removeTableRow = (tableId, rowId) => {
    const table = this.tables.get(tableId);
    if (!table) return;
    const removed = removeRowById(tableId, rowId);
    if (!removed) return;
    table.totalCount -= removed;
    Object.keys(this.blockIndex).forEach(blockId => {
      const block = this.blockIndex[blockId];
      if (block.type === BLOCK.TABLE_REF && block.tableId === tableId) {
        block.totalCount = table.totalCount;
      }
    });
    const lastPage = Math.max(1, Math.ceil(table.totalCount / table.pageSize));
    this.fetchTablePage(tableId, Math.min(table.currentPage, lastPage));
  };

  @action
  reset = () => {
    if (this.disconnectFn) {
      this.disconnectFn();
    }
    this.transport = null;
    this.disconnectFn = null;
    this.blockIndex = {};
    this.conversationId = null;
    this.conversationTitle = "";
    this.connected = false;
    this.messages = [];
    this.streaming = false;
    this.queuedMessages = [];
    this.awaiting = null;
    this.awaitingHint = null;
    this.suggestions = [];
    this.sidePanel = { open: false, tableId: null };
    this.previewPanel = { open: false, blockId: null };
    this.entityPanel = { open: false, blockId: null, itemId: null };
    this.tables = observable.map();
    resetTables();
  };

  findMessage = turnId => this.messages.find(message => message.id === turnId);

  // Rows-per-page switch in the table panel: refetch page 1 at the new size.
  @action
  setTablePageSize = (tableId, size) => {
    const table = this.tables.get(tableId);
    if (!table || !TABLE_PAGE_SIZES.includes(size) || table.pageSize === size) {
      return;
    }
    table.pageSize = size;
    this.fetchTablePage(tableId, 1);
  };

  registerTable = block => {
    if (this.tables.has(block.tableId)) return;
    this.tables.set(block.tableId, {
      title: block.title,
      columns: block.columns || [],
      totalCount: block.totalCount || 0,
      currentPage: 1,
      pageSize: PAGE_SIZE,
      rows: [],
      loading: false,
      notice: "",
      noticeType: "",
      // Restore spec, persisted with the block in chat history: enough for a
      // NEW server conversation to re-materialize this table (re-running the
      // stored search) after the conversation that built it was evicted. Rows
      // themselves are never persisted — every page is fetched live.
      kind: block.kind || "",
      args: block.args || null,
      searchId: block.searchId || "",
      blockId: block.id,
      restoreAttempted: false,
      // Selection rules travel with the block. "page" caps selection at the
      // visible rows (prospect results, where every row costs credits);
      // "all" allows selecting the whole matching set via `search`.
      selectable: !!block.selectable,
      selectionScope: block.selectionScope || "page",
      actions: block.actions || [],
      // Set when the table was opened to fill a specific sequence: the bulk
      // bar then enrols into it directly instead of asking which sequence.
      sequenceId: block.sequenceId || "",
      search: block.search || null,
      selectedIds: observable.array([]),
      // true = act on EVERY row matching `search`, not just the loaded page —
      // the Contacts page's semantics. The two are mutually exclusive.
      allMatchingSelected: false
    });
  };

  /** Toggle one row. Any explicit row change leaves select-all-matching mode. */
  @action
  toggleTableRow = (tableId, contactId) => {
    const table = this.tables.get(tableId);
    if (!table || !contactId) return;
    table.allMatchingSelected = false;
    const index = table.selectedIds.indexOf(contactId);
    if (index >= 0) table.selectedIds.splice(index, 1);
    else table.selectedIds.push(contactId);
  };

  /** Select or clear every row on the CURRENT page. */
  @action
  toggleTablePage = tableId => {
    const table = this.tables.get(tableId);
    if (!table) return;
    table.allMatchingSelected = false;
    const pageIds = table.rows.map(r => r.id).filter(Boolean);
    const allOn = pageIds.every(id => table.selectedIds.indexOf(id) >= 0);
    if (allOn) {
      table.selectedIds.replace(
        table.selectedIds.filter(id => pageIds.indexOf(id) < 0)
      );
    } else {
      const merged = table.selectedIds.slice();
      pageIds.forEach(id => {
        if (merged.indexOf(id) < 0) merged.push(id);
      });
      table.selectedIds.replace(merged);
    }
  };

  /** "Select all N matching" — acts via the filter, never via N ids. */
  @action
  selectAllMatching = (tableId, on = true) => {
    const table = this.tables.get(tableId);
    if (!table) return;
    // Page-scoped tables (unbought prospects) can never select beyond the rows
    // on screen — guarded here, not just hidden in the UI.
    if (table.selectionScope === "page") return;
    table.allMatchingSelected = on;
    if (on) {
      table.selectedIds.replace(table.rows.map(r => r.id).filter(Boolean));
    } else {
      table.selectedIds.clear();
    }
  };

  /**
   * Open a contact row in the detail panel with NO agent turn.
   *
   * The panel renders `entity_cards` items, so the row is wrapped in a
   * synthetic block rather than giving the panel a second shape to understand.
   * Everything shown comes from the row already on screen — no fetch, so this
   * is instant and free.
   */
  @action
  openContactFromTable = (tableId, row) => {
    if (!row || !row.id) return;
    const blockId = `contact_${tableId}_${row.id}`;
    // Saved contacts only. Prospect rows keep the original shape and the
    // original close behaviour.
    const table = this.tables.get(tableId);
    const isContacts = table && table.kind === "contacts";
    if (!this.blockIndex[blockId]) {
      this.blockIndex[blockId] = observable({
        id: blockId,
        type: BLOCK.ENTITY_CARDS,
        kind: "contact",
        items: [
          isContacts
            ? {
                // A row's `title` is the JOB title; the panel heading is the
                // person, so the two are remapped rather than shown as-is.
                ...row,
                title: row.name || row.title,
                subtitle: row.title || row.company || "",
                detail: { ...row }
              }
            : { ...row, detail: { ...row } }
        ]
      });
    }
    this.openEntityPanel(blockId, row.id, isContacts ? tableId : "");
  };

  // Set when "Add to list & export" was clicked: the export runs after the
  // add reports back, because the contacts do not exist until then.
  pendingListExport = null;

  setPendingListExport = blockId => {
    this.pendingListExport = blockId || null;
  };

  /**
   * "Add contacts" on a created sequence: ask the orchestrator to render the
   * contact table bound to that sequence. A view action — no model call.
   */
  // Sequence whose contacts are being fetched, so the chip can say so — the
  // round trip takes seconds and previously gave no sign it had started.
  @observable addContactsPending = "";
  // Bumped whenever an agent action finishes changing something. Pages observe
  // it to re-read what they show; it carries no payload on purpose, so a page
  // refreshes only what it owns.
  @observable mutationTick = 0;

  @action
  listContactsForSequence = sequenceId => {
    if (!sequenceId || !this.transport) return;
    this.addContactsPending = sequenceId;
    this.transport.send({
      type: CLIENT_EVENTS.LIST_CONTACTS_FOR_SEQUENCE,
      sequenceId
    });
  };

  /**
   * Export the list the prospects were just added to.
   *
   * Deliberately delayed: the contacts are indexed asynchronously after the
   * add, and exporting immediately returns an empty file.
   */
  exportListAfterAdd = async (listId, contactIds) => {
    if (!contactIds || !contactIds.length) return;
    const { exportContactsByIds, deleteExportList } = await import(
      "Pages/Dashboard/Copilot/agent/contactActions"
    );
    const { exportProcessingState } = await import(
      "Components/popup/ExportProcessing"
    );
    try {
      const result = await exportContactsByIds(contactIds);
      toasterState.setToastMsg(
        result && result.emailed
          ? "Too many to download here — the export will be emailed to you."
          : result && result.downloaded
            ? "Contacts have been exported successfully"
            : "The contacts were added, but the export could not be started.",
        result ? "success" : "failure"
      );
    } catch (err) {
      toasterState.setToastMsg(
        "The contacts were added, but the export could not be started.",
        "failure"
      );
    } finally {
      exportProcessingState.completeAnimation();
      exportProcessingState.setShowPopup(false);
      // The list existed only to scope this export.
      await deleteExportList(listId);
    }
  };

  /**
   * "Add to list & export" for prospects: give the export its own list so it
   * contains exactly the selection, then bin the list afterwards.
   */
  exportProspects = async (blockId, rows) => {
    const { createExportList, addProspectsToList, deleteExportList } =
      await import("Pages/Dashboard/Copilot/agent/contactActions");
    const { exportProcessingState } = await import(
      "Components/popup/ExportProcessing"
    );
    // The product's own export progress popup: this chain (create list -> add
    // -> index -> export) takes several seconds, and a button that finishes
    // instantly while nothing happens reads as a failure.
    exportProcessingState.setShowPopup(true);
    const list = await createExportList();
    if (!list) {
      exportProcessingState.setShowPopup(false);
      toasterState.setToastMsg(
        "Couldn't start the export. Please try again.",
        "failure"
      );
      return;
    }
    this.pendingListExport = blockId;
    const sent = addProspectsToList(this.transport, blockId, list.id, rows);
    if (!sent) {
      this.pendingListExport = null;
      exportProcessingState.setShowPopup(false);
      await deleteExportList(list.id);
      toasterState.setToastMsg(
        "None of the selected rows have a LinkedIn URL, so they can't be exported.",
        "failure"
      );
    }
  };

  /** The table whose block this is, so a block-level result can clear it. */
  tableIdForBlock = blockId => {
    let found = null;
    this.tables.forEach((table, id) => {
      if (table.blockId === blockId) found = id;
    });
    return found;
  };

  @action
  clearTableSelection = tableId => {
    const table = this.tables.get(tableId);
    if (!table) return;
    table.allMatchingSelected = false;
    table.selectedIds.clear();
  };

  // A table_ref patch (e.g. corrections dropped totalCount 3210 -> 3000)
  // must reflect in the open side panel: update the count, clamp the page,
  // and refetch so removed rows disappear.
  syncTableFromBlock = block => {
    const table = this.tables.get(block.tableId);
    if (!table) return;
    table.totalCount = block.totalCount;
    // Patched in after the block exists (the "Add contacts" flow binds the
    // sequence to a table that was already rendered).
    if (block.sequenceId) table.sequenceId = block.sequenceId;
    const lastPage = Math.max(1, Math.ceil(table.totalCount / table.pageSize));
    const page = Math.min(table.currentPage, lastPage);
    if (this.sidePanel.open && this.sidePanel.tableId === block.tableId) {
      this.fetchTablePage(block.tableId, page);
    } else {
      table.currentPage = page;
      table.rows = [];
    }
  };
}

export const copilotStore = new CopilotStore();
export default copilotStore;
